import i18n from '@/i18n';
/**
 * 刊登任务结果 → 商品库本地 meta（listed / listingJobPending）回写。
 * 商品库刊登状态存在浏览器本地；任务在后端完成后需主动同步。
 *
 * 注意：后端仅在刊登成功后写入 targetShopId；可用其校准「已刊登」，避免本地 meta 被误清。
 */

import { platformAPI } from '@/utils/api';
import {
  formatListingJobLineLog,
  listingJobLinePlatformSkcs,
  listingJobLinePlatformSpu,
} from './listingJobBatchView';
import {
  listingInfoPatchFromJob,
  mergeListingInfoFields,
  needsListingInfoBackfill,
} from './listingJobListingInfo';
import { ensureTemuPublishRecordsFromListingJobDetail } from './ensureTemuPublishRecordsFromListingJob';
import {
  getProductMeta,
  readMetaMap,
  setProductMeta,
} from './platformListingProductMeta';
import {
  clearStaleListingJobMeta,
  isListingJobNotFoundError,
} from './listingJobFetchUtils';

function firstText(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

function shopNameFromList(shops, shopId) {
  const id = String(shopId || '').trim();
  if (!id) return '';
  const shop = (Array.isArray(shops) ? shops : []).find((row) => String(row?.id) === id);
  return firstText(shop?.shopName, shop?.name);
}

export function listingStatusFromMeta(meta) {
  // 刊登中优先于已刊登（重新提交任务时）
  if (meta?.listingJobPending) return 'listing';
  if (meta?.listed) return 'listed';
  if (meta?.listingFailed) return 'failed';
  return 'pending';
}

export function listingStatusLabel(status) {
  if (status === 'listed') return i18n.t('workbench:studioChrome.listed');
  if (status === 'listing') return i18n.t('workbench:studioChrome.listing');
  if (status === 'failed') return i18n.t('workbench:studioChrome.listFailed');
  return i18n.t('workbench:studioChrome.listPending');
}

/** 行是否已在服务端归属店铺（刊登成功才会写） */
export function hasListingTargetShop(row) {
  const id = row?.targetShopId ?? row?.target_shop_id
    ?? row?.targetPlatformShopId ?? row?.target_platform_shop_id;
  return id != null && String(id).trim() !== '' && Number(id) > 0;
}

/**
 * 解析展示用刊登状态：本地 meta + 服务端 targetShopId。
 * @returns {'listed'|'listing'|'pending'|'failed'}
 */
export function resolveListingStatus(row, meta = null) {
  const m = meta || (row?.id != null ? getProductMeta(row.id) : null);
  if (m?.listingJobPending) return 'listing';
  if (m?.listed) return 'listed';
  if (hasListingTargetShop(row)) return 'listed';
  if (m?.listingFailed) return 'failed';
  return 'pending';
}

function listingLogPatchFromLine(line, jobId) {
  const errorMessage = String(line?.errorMessage || line?.error_message || '').trim();
  return {
    listingJobId: jobId,
    listingLog: formatListingJobLineLog(line),
    listingPlatformSpu: listingJobLinePlatformSpu(line),
    listingPlatformSkcs: listingJobLinePlatformSkcs(line),
    listingError: errorMessage,
  };
}

/**
 * 用服务端已分配店铺 + 条目上的类目/模板回填本地 meta。
 * @returns {number} 回填条数
 */
export function healListedMetaFromItems(items = [], shops = []) {
  let healed = 0;
  (Array.isArray(items) ? items : []).forEach((row) => {
    if (!row?.id) return;
    const meta = getProductMeta(row.id);
    const shopId = firstText(meta.listingShopId, row.targetShopId, row.target_shop_id);
    const patch = mergeListingInfoFields(meta, {
      listingShopId: shopId,
      listingShopName: shopNameFromList(shops, shopId),
      listingCategoryPathLabel: firstText(row.listingCategoryPathLabel, row.listing_category_path_label),
      listingProductTemplateId: firstText(row.listingProductTemplateId, row.listing_product_template_id),
      listingProductTemplateName: firstText(row.listingProductTemplateName, row.listing_product_template_name),
    });
    const shouldList = hasListingTargetShop(row) && !meta.listed && !meta.listingJobPending;
    const infoChanged = needsListingInfoBackfill(meta)
      && (
        patch.listingShopName !== firstText(meta.listingShopName)
        || patch.listingCategoryPathLabel !== firstText(meta.listingCategoryPathLabel)
        || patch.listingProductTemplateName !== firstText(meta.listingProductTemplateName)
        || patch.listingShopId !== firstText(meta.listingShopId)
      );
    if (!shouldList && !infoChanged) return;
    setProductMeta(row.id, {
      ...(shouldList ? { listed: true, listingJobPending: false } : {}),
      ...patch,
    });
    healed += 1;
  });
  return healed;
}

/**
 * 根据刊登任务详情回写关联商品库条目的本地状态。
 * @param {object} jobDetail getListingJob 返回
 * @returns {{ updated: number, listed: number, failed: number, pending: number }}
 */
function isListingJobTerminalStatus(jobStatus) {
  return jobStatus === 'done'
    || jobStatus === 'partial'
    || jobStatus === 'failed'
    || jobStatus === 'cancelled';
}

/** 已离开「转换/排队/推送中」：本地应停止 4s 轮询（定时上架也不再热轮询） */
function shouldStopListingJobPendingPoll(jobStatus) {
  return isListingJobTerminalStatus(jobStatus) || jobStatus === 'scheduled';
}

export function applyListingJobDetailToProductMeta(jobDetail) {
  const jobId = jobDetail?.id != null ? Number(jobDetail.id) : null;
  const lines = Array.isArray(jobDetail?.lines) ? jobDetail.lines : [];
  const infoFromJob = listingInfoPatchFromJob(jobDetail);
  let listed = 0;
  let failed = 0;
  let pending = 0;
  let updated = 0;

  const jobStatus = String(jobDetail?.status || '').toLowerCase();
  const jobTerminalFailed = jobStatus === 'failed' || jobStatus === 'cancelled';
  const stopPendingPoll = shouldStopListingJobPendingPoll(jobStatus);

  lines.forEach((line) => {
    const itemId = line?.sourcingCaptureItemId ?? line?.sourcing_capture_item_id;
    if (itemId == null) return;
    const status = String(line.lineStatus || line.line_status || '').toLowerCase();
    const logPatch = listingLogPatchFromLine(line, jobId);
    const prev = getProductMeta(itemId);
    const infoPatch = mergeListingInfoFields(prev, infoFromJob);
    if (status === 'listed') {
      const recolorKeys = Array.isArray(prev.pendingRecolorColorKeys)
        ? prev.pendingRecolorColorKeys.filter(Boolean)
        : [];
      const prevColorKeys = Array.isArray(prev.publishColorKeys) ? prev.publishColorKeys : null;
      const mergedColorKeys = recolorKeys.length && prevColorKeys
        ? [...new Set([...prevColorKeys, ...recolorKeys])]
        : prevColorKeys;
      setProductMeta(itemId, {
        ...logPatch,
        ...infoPatch,
        listed: true,
        listingFailed: false,
        listingJobPending: false,
        ...(recolorKeys.length ? { pendingRecolorColorKeys: [] } : {}),
        ...(mergedColorKeys && mergedColorKeys !== prevColorKeys
          ? { publishColorKeys: mergedColorKeys }
          : {}),
      });
      listed += 1;
      updated += 1;
      return;
    }
    // 任务已失败/终止时，仍停在 preparing/pending 的行也按失败收尾，避免本地一直「处理中/转换中」
    if (status === 'failed' || (jobTerminalFailed && (status === 'preparing' || status === 'pending'))) {
      // 失败不得把「曾经已刊登」打回待刊登（商品可能已在店铺侧存在）
      setProductMeta(itemId, {
        ...logPatch,
        ...infoPatch,
        listed: Boolean(prev.listed),
        listingFailed: !prev.listed,
        listingJobPending: false,
        pendingRecolorColorKeys: [],
      });
      failed += 1;
      updated += 1;
      return;
    }
    // 任务已终态/已交定时：无论行状态如何都停轮询，避免同批 jobId 永久打 getListingJob
    if (stopPendingPoll) {
      setProductMeta(itemId, {
        ...logPatch,
        ...infoPatch,
        listed: Boolean(prev.listed) || status === 'listed',
        listingFailed: Boolean(prev.listingFailed) && !prev.listed,
        listingJobPending: false,
        listingJobId: jobId,
        pendingRecolorColorKeys: [],
      });
      updated += 1;
      return;
    }
    // pending / processing / preparing / queued：保持刊登中
    setProductMeta(itemId, {
      ...infoPatch,
      listed: Boolean(prev.listed),
      listingJobPending: true,
      listingJobId: jobId,
    });
    pending += 1;
    updated += 1;
  });

  // 无行明细时：终态/定时任务也要把绑定该 jobId 的 pending 清掉
  if (lines.length === 0 && jobId != null && stopPendingPoll) {
    const map = readMetaMap();
    Object.keys(map).forEach((id) => {
      const row = map[id];
      if (!row || Number(row.listingJobId) !== jobId) return;
      if (!row.listingJobPending && !needsListingInfoBackfill(row)) return;
      const infoPatch = mergeListingInfoFields(row, infoFromJob);
      setProductMeta(id, {
        listed: Boolean(row.listed),
        listingFailed: !row.listed && jobTerminalFailed ? true : Boolean(row.listingFailed),
        listingJobPending: false,
        listingJobId: jobId,
        ...infoPatch,
      });
      updated += 1;
      if (jobTerminalFailed) failed += 1;
    });
  }

  void ensureTemuPublishRecordsFromListingJobDetail(jobDetail).catch(() => {});

  return { updated, listed, failed, pending };
}

/**
 * 只拉仍标记 listingJobPending 的任务并回写终态。
 * 已刊登补店铺/类目信息不要走热轮询，否则会把历史 jobId 永久刷爆 Network。
 * @returns {Promise<{ checkedJobs: number, updated: number }>}
 */
export async function reconcilePendingListingProductMetas() {
  const map = readMetaMap();
  const jobIds = new Set();
  Object.keys(map).forEach((id) => {
    const meta = getProductMeta(id);
    if (!meta.listingJobPending) return;
    if (meta.listingJobId != null && Number.isFinite(Number(meta.listingJobId))) {
      jobIds.add(Number(meta.listingJobId));
    }
  });

  let updated = 0;
  let checkedJobs = 0;
  for (const jobId of jobIds) {
    checkedJobs += 1;
    try {
      // eslint-disable-next-line no-await-in-loop
      const detail = await platformAPI.getListingJob(jobId);
      const result = applyListingJobDetailToProductMeta(detail);
      updated += result.updated;
    } catch (err) {
      if (isListingJobNotFoundError(err)) {
        clearStaleListingJobMeta(jobId);
      }
    }
  }
  return { checkedJobs, updated };
}
